import React, { useState } from "react";
import axios from "axios";
import { X, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BuyNowModal = ({ product, open, onClose }) => {
  const [size, setSize] = useState(product?.sizes?.[0] || "");
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);

  if (!open || !product) return null;

  const price = product.variant_prices?.[size] || product.price || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        ...form,
        items: [{ slug: product.slug, name: product.name, size, qty, price }],
      });
      trackClick(`order-placed-${product.slug}`);
      setPlaced(data.order_no);
    } catch {
      toast.error("Could not place order — please try again or WhatsApp us.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="buy-modal">
        <div className="flex items-center justify-between p-6 border-b border-[#3B2A1A]/10">
          <div>
            <p className="overline">Place Order</p>
            <p className="font-serif-display text-xl text-[#3B2A1A]">{product.name}</p>
          </div>
          <button onClick={onClose} className="text-[#8A7969] hover:text-[#3B2A1A]"><X size={20} /></button>
        </div>

        {placed ? (
          <div className="p-8 text-center" data-testid="order-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#C89D3C]/10 flex items-center justify-center">
              <ShoppingBag size={22} className="text-[#C89D3C]" />
            </div>
            <p className="font-serif-display text-2xl text-[#3B2A1A] mt-5">Order received.</p>
            <p className="text-sm text-[#5C4A3A] mt-3 font-light">Your order number is</p>
            <p className="font-serif-display text-3xl text-[#C89D3C] mt-2 tracking-wider">{placed}</p>
            <p className="text-sm text-[#5C4A3A] mt-6 font-light">Our team will call you within 24 hours to confirm and arrange delivery.</p>
            <button onClick={onClose} className="btn-primary mt-8">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-5" data-testid="buy-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Size</label>
                <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="buy-size"
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]">
                  {(product.sizes || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Qty</label>
                <input required type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} data-testid="buy-qty"
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
              </div>
            </div>
            {price && (
              <div className="bg-[#F5EFE3] p-4 flex items-center justify-between border border-[#3B2A1A]/10">
                <span className="text-xs uppercase tracking-widest text-[#8A7969]">Estimated total</span>
                <span className="font-serif-display text-2xl text-[#3B2A1A]" data-testid="buy-total">
                  ₹ {(() => { const n = parseFloat(String(price).replace(/[^\d.]/g, "")); return isNaN(n) ? price : (n * qty).toLocaleString("en-IN"); })()}
                </span>
              </div>
            )}

            {[["name", "Full Name"], ["phone", "Phone"], ["email", "Email (optional)"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">{l}</label>
                <input required={k !== "email"} type={k === "email" ? "email" : "text"} value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`buy-input-${k}`}
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Delivery Address</label>
              <textarea required rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="buy-input-address"
                className="w-full bg-transparent border border-[#3B2A1A]/20 p-3 mt-2 text-sm focus:outline-none focus:border-[#C89D3C]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">{l}</label>
                  <input required={k !== "state"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`buy-input-${k}`}
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
              ))}
            </div>

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="buy-submit-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <>Place Order <ShoppingBag size={14} /></>}
            </button>
            <p className="text-[10px] text-center text-[#8A7969] font-light">
              This is a Cash-on-Delivery style order. Our team calls to confirm before dispatch.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default BuyNowModal;
